import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Upload, Link as LinkIcon, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useShortsAccess } from '../hooks/useShortsAccess';
import { requestUploadUrl, uploadFileResumable, triggerJob, checkDriveAccess } from '../lib/valuebellApi';
import { parseDriveFileUrl } from '../lib/driveValidation';
import { useDriveFileInfo } from '../hooks/useDriveFileInfo';
import { uploadError, uploadInfo, driveFileError, resolveFormat, extension, formatFileSize, AUDIO_SHORTS_MESSAGE, UNKNOWN_SHORTS_MESSAGE } from '../lib/mediaPolicy';

export const Hero = ({ embedded = false }) => {
  const submitting = useRef(false);
  const [activeTab, setActiveTab] = useState('link');
  const [link, setLink] = useState('');
  const [file, setFile] = useState(null);
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);
  const [shouldCreateMapping, setShouldCreateMapping] = useState(false);
  const [shouldCreateShorts, setShouldCreateShorts] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | preparing | uploading | triggering | success | error
  const [progress, setProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState(null);
  const [lastJobId, setLastJobId] = useState(null);

  const { user, loginWithGoogle } = useAuth();
  const { hasAccess: hasShortsAccess } = useShortsAccess(user);

  const isBusy = ['validating', 'preparing', 'uploading', 'triggering'].includes(status);

  // Real-time format validation for the link tab
  const linkParsed = parseDriveFileUrl(link);
  const driveInfo = useDriveFileInfo(user, activeTab === 'link' && linkParsed.valid ? linkParsed.fileId : null);
  const selectedInfo = activeTab === 'upload' ? uploadInfo(file) : driveInfo.file;
  const selectedUploadError = activeTab === 'upload' && file ? uploadError(file) : null;
  const driveError = activeTab === 'link' && user && linkParsed.valid && !driveInfo.loading
    ? driveInfo.error || (driveInfo.accessible !== true ? 'Cannot access this Drive file. Check its sharing settings.' : driveFileError(driveInfo.file)) : null;
  useEffect(() => {
    if (driveError) console.warn('[Valuebell] Drive file rejected:', driveError);
  }, [driveError]);
  const sourceReady = activeTab === 'upload' ? Boolean(file) && !selectedUploadError
    : linkParsed.valid && !driveInfo.loading && driveInfo.accessible === true && !driveError;
  const selectedFormat = activeTab === 'link' ? resolveFormat(selectedInfo?.fileName, selectedInfo?.mimeType) : extension(selectedInfo?.fileName);
  const shortsEligible = sourceReady && selectedFormat === 'mp4';
  const shortsBlocked = Boolean(selectedInfo && selectedFormat !== 'mp4');
  useEffect(() => {
    if (shortsBlocked) setShouldCreateShorts(false);
  }, [shortsBlocked]);
  const hasLinkInput = link.trim().length > 0;
  const showLinkError = activeTab === 'link' && hasLinkInput && !linkParsed.valid;
  const hasAnyLinkError = showLinkError || (activeTab === 'link' && status === 'error' && errorMsg);
  const isDisabled = isBusy || (user ? !sourceReady : showLinkError || Boolean(selectedUploadError));

  const linkErrorMsg = showLinkError
    ? {
        folder:
          'This looks like a folder link. Open the file in Drive, click Share → Copy link, and paste that link here.',
        invalid:
          'Only Google Drive file links are accepted (drive.google.com/file/d/…)',
      }[linkParsed.reason] ?? 'Invalid link'
    : null;

  const resetStatus = () => {
    setStatus('idle');
    setProgress(0);
    setErrorMsg(null);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selected = e.target.files[0];
      setFile(selected);
      resetStatus();
      const error = uploadError(selected);
      if (error) { setErrorMsg(error); setStatus('error'); }
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting.current || isBusy) return;
    submitting.current = true;

    // Require auth first
    if (!user) {
      try {
        await loginWithGoogle();
      } catch (err) {
        setErrorMsg('Sign in is required to continue');
        setStatus('error');
      } finally { submitting.current = false; }
      return;
    }

    setErrorMsg(null);
    setLastJobId(null);

    try {
      if (!sourceReady) throw new Error(selectedUploadError || driveError || 'Select a supported file and wait for verification before submitting.');
      if (activeTab === 'link') {
        const trimmed = link.trim();

        // 1. Format validation (inline errors cover most cases, this catches empty)
        if (!linkParsed.valid) {
          setErrorMsg(linkParsed.reason === 'empty' ? 'Please paste a Google Drive link' : 'Invalid link');
          setStatus('error');
          return;
        }

        const { fileId } = linkParsed;

        // 2. Async access check
        setStatus('validating');
        let accessible;
        let driveFile;
        try {
          ({ accessible, file: driveFile } = await checkDriveAccess(user, fileId));
        } catch (err) {
          console.warn('[Valuebell] Drive access check failed:', err);
          setErrorMsg(
            'Could not verify file access — please make sure the file is shared as "Anyone with the link".',
          );
          setStatus('error');
          return;
        }

        if (!accessible) {
          setErrorMsg(
            'This file is not publicly accessible. In Google Drive, right-click the file → Share → change access to "Anyone with the link can view", then try again.',
          );
          setStatus('error');
          return;
        }

        const driveValidationError = driveFileError(driveFile);
        if (driveValidationError) throw new Error(driveValidationError);

        // 3. All good — trigger the job
        setStatus('triggering');
        const { jobId } = await triggerJob(user, {
          driveVideoUrl: trimmed,
          userId: user.uid,
          email: user.email,
          userName: user.displayName || user.email,
          episodeName: null,
          newsletterOptIn,
          shouldCreateShorts: shouldCreateShorts && resolveFormat(driveFile.fileName, driveFile.mimeType) === 'mp4',
          shouldCreateMapping,
        });
        console.log('[Valuebell] Job triggered (drive):', jobId);
        setLastJobId(jobId);
        setLink('');
        setStatus('success');
      } else {
        if (!file) {
          setErrorMsg('Please select a file to upload');
          setStatus('error');
          return;
        }
        const validationError = uploadError(file);
        if (validationError) throw new Error(validationError);
        // 1. Get signed upload URL
        setStatus('preparing');
        const { uploadUrl, gcsPath } = await requestUploadUrl(user, file);

        // 2. Resumable upload to GCS
        setStatus('uploading');
        setProgress(0);
        await uploadFileResumable(uploadUrl, file, (pct) => setProgress(pct));

        // 3. Trigger the job
        setStatus('triggering');
        const { jobId } = await triggerJob(user, {
          gcsPath,
          originalFileName: file.name,
          userId: user.uid,
          email: user.email,
          userName: user.displayName || user.email,
          episodeName: null,
          newsletterOptIn,
          shouldCreateShorts: shouldCreateShorts && shortsEligible,
          shouldCreateMapping,
        });
        console.log('[Valuebell] Job triggered (upload):', jobId);
        setLastJobId(jobId);
        setFile(null);
        setStatus('success');
      }
    } catch (err) {
      console.error('[Valuebell] Submit error:', err);
      setErrorMsg(err?.message || 'Something went wrong');
      setStatus('error');
    } finally { submitting.current = false; }
  };

  const buttonLabel = () => {
    if (!user) return 'Sign in to get clips';
    switch (status) {
      case 'validating':
        return 'Checking file access…';
      case 'preparing':
        return 'Preparing upload…';
      case 'uploading':
        return `Uploading ${progress}%`;
      case 'triggering':
        return 'Starting job…';
      case 'success':
        return 'Submit another';
      default:
        return 'Submit';
    }
  };

  return (
    <section className={`relative ${embedded ? "pt-4 sm:pt-6" : "pt-20 sm:pt-24"} pb-4 sm:pb-6 px-4 sm:px-6 lg:px-8 flex items-center`}>
      <div className="relative max-w-4xl mx-auto text-center w-full">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-2 sm:mb-3 leading-tight">
          VALUEBOT
        </h1>

        <p className="text-sm sm:text-lg text-slate-300 mb-6 sm:mb-8 leading-relaxed px-2">
          Enter your podcast and we will do the rest
        </p>

        {/* Form Card */}
        <div className="bg-brand-surface/70 backdrop-blur-xl border border-brand-surface-3 rounded-2xl p-4 sm:p-5 shadow-2xl max-w-md mx-auto">
          <form onSubmit={handleSubmit}>
            {/* Tab Selector */}
            <div className="flex gap-2 sm:gap-3 mb-4 sm:mb-5">
              <button
                type="button"
                onClick={() => { setActiveTab('link'); resetStatus(); }}
                disabled={isBusy}
                className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl transition-all duration-200 font-medium text-xs sm:text-base disabled:opacity-50 ${
                  activeTab === 'link'
                    ? 'bg-brand-surface-2 text-white'
                    : 'bg-transparent text-slate-400 hover:text-white hover:bg-brand-surface-2/50'
                }`}
              >
                <LinkIcon className="w-4 h-4" />
                Drop a drive link
              </button>
              <button
                type="button"
                onClick={() => { setActiveTab('upload'); resetStatus(); }}
                disabled={isBusy}
                className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl transition-all duration-200 font-medium text-xs sm:text-base disabled:opacity-50 ${
                  activeTab === 'upload'
                    ? 'bg-brand-surface-2 text-white'
                    : 'bg-transparent text-slate-400 hover:text-white hover:bg-brand-surface-2/50'
                }`}
              >
                <Upload className="w-4 h-4" />
                Upload
              </button>
            </div>

            {/* Input */}
            {activeTab === 'link' && (
              <div className="mb-4 sm:mb-5">
                <input
                  type="text"
                  value={link}
                  onChange={(e) => { setLink(e.target.value); resetStatus(); }}
                  placeholder="Paste your Google Drive file link"
                  disabled={isBusy}
                  className={`w-full px-4 sm:px-5 py-3 sm:py-3.5 bg-brand-surface-2/80 border rounded-xl text-white placeholder-slate-400 focus:outline-none transition-colors duration-200 text-sm sm:text-base disabled:opacity-60 ${
                    hasAnyLinkError
                      ? 'border-red-500/60 focus:border-red-500'
                      : 'border-brand-surface-3 focus:border-orange-500'
                  }`}
                />

              </div>
            )}

            {activeTab === 'upload' && (
              <div className="mb-4 sm:mb-5">
                <label className={`flex flex-col items-center justify-center w-full px-4 sm:px-5 py-5 sm:py-7 bg-brand-surface-2/80 border-2 border-dashed border-brand-surface-3 rounded-xl transition-colors duration-200 ${isBusy ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:border-orange-500'}`}>
                  <Upload className="w-7 h-7 sm:w-9 sm:h-9 text-slate-400 mb-2" />
                  <span className="text-slate-300 text-sm sm:text-base text-center break-all px-2">
                    {file ? file.name : 'Click to upload a file'}
                  </span>
                  <span className="text-slate-500 text-xs mt-1">MP3, MP4, or WAV · Maximum 25 GB per file</span>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    className="hidden"
                    accept=".mp3,.mp4,.wav"
                    disabled={isBusy}
                  />
                </label>
              </div>
            )}

            {activeTab === 'link' && linkParsed.valid && !user && (
              <p className="text-xs text-slate-400 mb-3">Sign in to check the Drive file's type and size.</p>
            )}
            {activeTab === 'link' && driveInfo.loading && <p role="status" className="text-xs text-slate-400 mb-3">Checking file information…</p>}
            {selectedInfo && !selectedUploadError && !driveError && (
              <div className="text-left text-xs text-slate-300 mb-3 break-words" aria-live="polite">
                <p>{selectedInfo.fileName} · {formatFileSize(selectedInfo.size)}</p>
                <p>{shortsEligible ? 'MP4 video — shorts available' : selectedInfo.mediaKind === 'audio' ? 'Audio file — transcription and show notes available' : UNKNOWN_SHORTS_MESSAGE}</p>
              </div>
            )}
            {(linkErrorMsg || selectedUploadError || driveError || (status === 'error' && errorMsg) || (activeTab === 'link' && (driveInfo.error || driveInfo.accessible === false))) && (
              <div role="alert" className="mb-4 flex items-start gap-2 text-left p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-300 break-words">{linkErrorMsg || selectedUploadError || driveError || errorMsg || driveInfo.error || 'Cannot access this Drive file. Check its sharing settings.'}</p>
              </div>
            )}

            {activeTab === 'link' && <p className="text-xs text-slate-400 mb-3">MP3, MP4, or WAV only.{hasShortsAccess ? ' Shorts are available for MP4 only.' : ''}</p>}

            {/* Checkboxes */}
            <div className="space-y-2.5 mb-4 sm:mb-5">
              <label className="flex items-center gap-2.5 text-left cursor-pointer select-none">
                <input type="checkbox" checked={shouldCreateMapping} onChange={(e) => setShouldCreateMapping(e.target.checked)} disabled={isBusy || shouldCreateShorts} className="w-4 h-4 accent-orange-500" />
                <span className="text-xs sm:text-sm text-slate-300">Create show notes</span>
              </label>
              {shouldCreateShorts && <p className="text-xs text-slate-400 text-left">Shorts require show notes.</p>}
              {hasShortsAccess && (
                <label className="flex items-center gap-2.5 text-left cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={shouldCreateShorts}
                    onChange={(e) => { setShouldCreateShorts(e.target.checked); if (e.target.checked) setShouldCreateMapping(true); }}
                    disabled={isBusy || shortsBlocked}
                    className="w-4 h-4 rounded border-brand-surface-3 bg-brand-surface-2 text-orange-500 focus:ring-orange-500 focus:ring-offset-0 cursor-pointer accent-orange-500 flex-shrink-0"
                  />
                  <span className="text-xs sm:text-sm text-slate-300">
                    Automatically create shorts 🔥
                  </span>
                </label>
              )}

              {hasShortsAccess && selectedInfo?.mediaKind === 'audio' && <p className="text-xs text-slate-400 text-left">{AUDIO_SHORTS_MESSAGE}</p>}

              <label className="flex items-center gap-2.5 text-left cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={newsletterOptIn}
                  onChange={(e) => setNewsletterOptIn(e.target.checked)}
                  disabled={isBusy}
                  className="w-4 h-4 rounded border-brand-surface-3 bg-brand-surface-2 text-orange-500 focus:ring-orange-500 focus:ring-offset-0 cursor-pointer accent-orange-500 flex-shrink-0"
                />
                <span className="text-xs sm:text-sm text-slate-300">
                  Send me updates and tips about new features
                </span>
              </label>
            </div>

            {/* Progress bar (only during upload) */}
            {status === 'uploading' && (
              <div className="mb-4 sm:mb-5">
                <div className="h-2 w-full bg-brand-surface-2 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-orange-400 to-red-500 transition-all duration-200"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-xs text-slate-400 mt-2 text-left">
                  Uploading to secure storage… {progress}%
                </p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isDisabled}
              className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-white text-slate-900 rounded-xl hover:bg-slate-100 transition-all duration-300 font-bold text-base sm:text-lg shadow-lg hover:shadow-xl hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
            >
              {isBusy && <Loader2 className="w-5 h-5 animate-spin" />}
              {buttonLabel()}
            </button>

            <p className="text-center text-xs text-slate-400 mt-3">
              By submitting, you agree to our{' '}
              <Link to="/privacy" className="text-orange-400 hover:underline">
                Privacy Policy
              </Link>{' '}
              and{' '}
              <Link to="/terms" className="text-orange-400 hover:underline">
                Terms of Service
              </Link>
            </p>

            {/* Feedback */}
            {status === 'success' && (
              <div className="mt-4 flex items-start gap-2 text-left p-3 bg-green-500/10 border border-green-500/30 rounded-xl">
                <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <div className="text-xs sm:text-sm">
                  <p className="text-green-300 font-medium">Job started successfully</p>
                </div>
              </div>
            )}


          </form>
        </div>
      </div>
    </section>
  );
};
